/**
 * Task 161 — Package Builder UX: service-family layer over the 6 backend
 * `PackageServiceType` discriminators. Pure, no React, no API.
 *
 * The drawer's primary UX is built around five **sellable** families that
 * match how the agency actually talks about services, not the internal
 * discriminator vocabulary. The backend `kind` (social / ads / influencer /
 * production / content / brand) is preserved as-is on the wire and in the
 * `config jsonb`; this module is a UI overlay that derives the family from
 * the kind and supplies labels + an automation-badge tone per family.
 *
 * `'custom'` is a defensive fallback — every current `PackageServiceType`
 * maps cleanly to one of the four real families.
 */

import type { PackageServiceType } from '@kmi/contracts';
import type { SystemCapabilityKey } from '../../api/types';

export type PackageServiceFamily =
  | 'social_media'
  | 'paid_campaigns'
  | 'content_production'
  | 'influencers'
  | 'custom';

/**
 * Capability keys that map cleanly to one of the four typed families via
 * `kind`. Any other capability (`strategy` / `reports` / `crm` / `calendar` /
 * `client_onboarding` / `custom`) falls through `defaultKindForCapability`'s
 * default branch to `kind='content'`, and would otherwise be MISLABELED as
 * `content_production`. Those surface as `custom` instead.
 */
const TYPED_CAPABILITIES: ReadonlySet<SystemCapabilityKey> = new Set<SystemCapabilityKey>([
  'publishing',
  'paid_media',
  'influencers',
  'creative',
  'content',
]);

/**
 * Map a service line to its sellable UI family. Family is driven by both the
 * backend `kind` discriminator AND the service's `system_capability_key`: a
 * line whose capability is off the typed-family list (e.g. `custom`,
 * `strategy`, `reports`) surfaces as the `custom` family regardless of kind,
 * so the manager gets a deliverables-only UX rather than a misleading
 * content-production header. When the capability is known, the four typed
 * families are derived purely from the kind.
 */
export function familyForKind(
  kind: PackageServiceType,
  capability?: SystemCapabilityKey,
): PackageServiceFamily {
  // An off-list capability surfaces as Custom ONLY when the kind is the
  // default `content` fallthrough from `defaultKindForCapability`. An
  // EXPLICIT kind override (a stored `config.kind` or a power-user choice
  // in the advanced selector — e.g. `social` / `ads`) honors the override
  // and drives the family from the kind, so the family overlay can never
  // disagree with the explicit kind (Codex round-2 P2).
  if (capability !== undefined && !TYPED_CAPABILITIES.has(capability) && kind === 'content') {
    return 'custom';
  }
  switch (kind) {
    case 'social':
      return 'social_media';
    case 'ads':
      return 'paid_campaigns';
    case 'influencer':
      return 'influencers';
    case 'production':
    case 'content':
    case 'brand':
      return 'content_production';
    default:
      return 'custom';
  }
}

/**
 * What renders as the per-line MAIN flow inside the drawer:
 *  - `typed-config`     : the typed sub-config (`ServiceConfigSection`) —
 *                         platforms / monthly+weekly quotas / strategy / reply
 *                         for social, pricing / amount+percentage / platforms /
 *                         details for paid. These ARE the deliverables for
 *                         these families, so we do NOT also render the generic
 *                         `DeliverablesEditor`.
 *  - `influencer-scope` : the minimal influencer scope block (`InfluencerScopeSection`)
 *                         — a workflow-later banner + a single scope textarea
 *                         bound to `config.details`. Hides `count` / `category`
 *                         from editing; their persisted values round-trip
 *                         untouched.
 *  - `deliverables`     : the generic `DeliverablesEditor` (Task 161 behavior).
 *                         Right primary UX for content_production (each
 *                         deliverable is a discrete production output) and
 *                         custom (only way to describe a freeform service).
 */
export type MainFlowMode = 'typed-config' | 'influencer-scope' | 'deliverables';

export interface PackageFamilyLayout {
  /** What renders as the per-line MAIN block. */
  readonly mainFlow: MainFlowMode;
  /**
   * Does the advanced section ALSO render `ServiceConfigSection` for the raw
   * typed config? True only for families whose main flow does NOT already
   * show it (the deliverables families). For typed-config and
   * influencer-scope families, the typed config is fully addressed in the
   * main flow (or intentionally simplified for influencers) and the
   * advanced ServiceConfigSection would be redundant or expose the
   * "fake workflow" fields the influencer spec rules out.
   */
  readonly showTypedConfigInAdvanced: boolean;
}

/**
 * Task 161B — per-family drawer layout descriptor. Pure mapping; the drawer
 * dispatches on this to decide what renders in main vs advanced. Splitting
 * this out as a typed enum-style map (instead of a boolean `isAutomatedFamily`)
 * lets the influencer family carry its own "minimal scope + workflow-later
 * note" UX without dragging the deliverables rows in.
 */
export function familyLayout(family: PackageServiceFamily): PackageFamilyLayout {
  switch (family) {
    case 'social_media':
    case 'paid_campaigns':
      return { mainFlow: 'typed-config', showTypedConfigInAdvanced: false };
    case 'influencers':
      return { mainFlow: 'influencer-scope', showTypedConfigInAdvanced: false };
    case 'content_production':
    case 'custom':
      return { mainFlow: 'deliverables', showTypedConfigInAdvanced: true };
  }
}

/**
 * Convenience alias used by callers that only care whether the typed sub-config
 * leads the main flow (social_media + paid_campaigns). Kept thin so the Task
 * 161 pin / call sites do not break, and derived from `familyLayout` so it
 * cannot drift.
 */
export function isAutomatedFamily(family: PackageServiceFamily): boolean {
  return familyLayout(family).mainFlow === 'typed-config';
}

/** Arabic agency-friendly label per family (drawer header + cards). */
export const PACKAGE_SERVICE_FAMILY_LABELS_AR: Readonly<Record<PackageServiceFamily, string>> = {
  social_media: 'إدارة السوشال ميديا',
  paid_campaigns: 'إدارة الحملات الإعلانية',
  content_production: 'إنتاج المحتوى',
  influencers: 'إدارة حملات المؤثرين',
  custom: 'خدمة مخصصة',
};

/**
 * Automation tone. Drives the badge color and the displayed copy:
 *  - `auto-count`  : publishing events auto-record package delivery consumption.
 *  - `auto-data`   : data is automated (Meta sync), but package consumption is
 *                    NOT auto-counted yet — manual until a delivery event exists.
 *  - `manual`      : workflow planned later; consumption is recorded manually.
 *  - `custom`      : freeform service — manual by default.
 */
export type PackageAutomationTone = 'auto-count' | 'auto-data' | 'manual' | 'custom';

export interface PackageAutomationBadge {
  readonly tone: PackageAutomationTone;
  /** Short pill copy. */
  readonly label: string;
  /** One-line muted explanation under the badge. */
  readonly description: string;
}

/**
 * The automation badge for a given family. The semantics here are
 * load-bearing — the publishing path is the ONLY family that currently
 * auto-counts package delivery consumption from system events; paid_campaigns
 * is data-automated but its package consumption is still manual until an
 * event surface exists; the rest are manual-until-workflow.
 */
export function automationBadgeForFamily(family: PackageServiceFamily): PackageAutomationBadge {
  switch (family) {
    case 'social_media':
      return {
        tone: 'auto-count',
        label: 'يُحتسب تلقائياً',
        description:
          'استهلاك الباقة يُسجَّل تلقائياً عند النشر من خلال المنصّة (publishing events).',
      };
    case 'paid_campaigns':
      return {
        tone: 'auto-data',
        label: 'بياناته مؤتمتة',
        description:
          'بيانات الأداء تُسحب تلقائياً من Meta، لكن استهلاك الباقة يُسجَّل يدوياً حالياً.',
      };
    case 'content_production':
      return {
        tone: 'manual',
        label: 'يدوي حالياً',
        description: 'يُسجَّل استهلاك الباقة يدوياً حتى يربط بسير عمل لاحقاً.',
      };
    case 'influencers':
      return {
        tone: 'manual',
        label: 'يدوي حالياً',
        description: 'يُسجَّل استهلاك الباقة يدوياً حتى يربط بسير عمل لاحقاً.',
      };
    case 'custom':
      return {
        tone: 'custom',
        label: 'مخصص',
        description: 'خدمة مخصّصة — يُسجَّل استهلاكها يدوياً.',
      };
  }
}
